import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import {
  DialogContent,
  Typography,
  Box,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  FormLabel,
  FormControlLabel,
  Tooltip,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import { spaces, palette, borderRadius } from '../../../../assets/styles/themes/tokens';
import { aiAssistantContext } from '../../../../state/AIAssistantContextProvider';
import {
  getFormConfig,
  FormField,
  FieldName,
  TextInputField,
  SelectField,
  MultiSelectField,
  AISuggestionsField,
  DependentSelectField,
  TemplateFormConfig,
  PodcastDuration,
  PodcastTargetLanguage,
  PodcastFormat,
} from './formConfigs';
import LabelledTextField from '../../../LabelledTextField/LabelledTextField';
import { useTranslation } from 'react-i18next';
import EmptyCheckbox from '../../../../assets/lernsets/icons/checkbox.svg';
import SelectedCheckbox from '../../../../assets/lernsets/icons/selectedCheckbox.svg';
import AISuggestionsFieldComponent from './AISuggestionsField';
import { ampliTrackAISuggestionsAppliedEvent } from '../../../../services/track.service';
import { ActiveView } from '../constants';
import { useSelector } from 'react-redux';
import AttachmentsButton from '../Footer/PromptInput/AttachmentsButton';
import AIUploadFileList from '../Footer/PromptInput/AIUploadFileList';
import {
  getTemporaryAttachments,
  getAiGeneratedImagesRemainingLimit,
  areTemporaryAttachmentsReady,
} from '../../../../reducers/aiAssistant.selectors';
import useAction from '../../../../utils/useAction';
import { clearTemporaryAttachments as clearTemporaryAttachmentsAction } from '../../../../actions/aiAssistant.actions';
import AIAddLinks from './AIAddLinks';
import useScreenSize from '../../../../utils/useScreenSize';
import useRouter from '../../../../utils/useRouter';
import { ConversationMessageContext, TemplatePrompts } from '@kimuno/api-sdk/lib/types/common/Conversation';
import { getAIAssistantPageSource } from '../../../../utils/getAIAssistantPageSource';

const fullWidthFieldTypes = ['text', 'aiSuggestions'];

const isFieldVisible = (field: FormField, values: Record<string, any>) =>
  !field.visibleWhen || values[field.visibleWhen.field] === field.visibleWhen.value;

const getDependentSelectDefault = (depField: DependentSelectField, parentValue: string) =>
  (depField.optionGroups[parentValue] || [])[depField.defaultOptionIndex ?? 0]?.value || '';

export const podcastDurationMap: Record<PodcastDuration, number> = {
  [PodcastDuration.SHORT]: 1,
  [PodcastDuration.MEDIUM]: 3,
  [PodcastDuration.LONG]: 5,
};

export const podcastLanguageMap: Record<PodcastTargetLanguage, string> = {
  [PodcastTargetLanguage.GERMAN]: 'de',
  [PodcastTargetLanguage.ENGLISH]: 'en',
  [PodcastTargetLanguage.FRENCH]: 'fr',
  [PodcastTargetLanguage.SPANISH]: 'es',
};

export const podcastFormatMap: Record<PodcastFormat, string> = {
  [PodcastFormat.DIALOG]: 'dialog',
  [PodcastFormat.EXPLANATION]: 'explainer',
  [PodcastFormat.STORYTELLING]: 'storytelling',
};

const IMAGE_GENERATION_ENABLED_FIELD = 'image_generation_enabled';
const IMAGE_DESCRIPTION_FIELD = FieldName.IMAGE_DESCRIPTION;

// Optional guidance on how the uploaded files should be used. Not a config
// field: it renders below the upload tiles, after every declared field, and its
// visibility keys off Redux attachment state rather than a form value.
// Removing the last file unmounts the input, which unregisters it in RHF, so the
// value falls back to the '' seeded below — no explicit reset needed (and an
// effect that calls setValue for this unregistered field would re-render forever,
// since `watch()` marks every name as watched).
const ATTACHMENTS_HINT_FIELD = 'attachmentsHint';
const ATTACHMENTS_HINT_MAX_LENGTH = 300;

// Build default values from config, optionally seeded by a launch prefill (dashboard
// Muki cards). Prefill only overrides text/select fields the template actually declares
// — a field a given template lacks is skipped, and no prefill leaves behavior unchanged.
const buildDefaultValues = (
  formConfig: TemplateFormConfig,
  prefill?: Partial<Record<FieldName, string>> | null,
) => {
  const defaultValues: Record<string, any> = {};

  formConfig.fields.forEach(field => {
    if (field.type === 'text' || field.type === 'aiSuggestions') {
      defaultValues[field.name] = '';
    } else if (field.type === 'select') {
      const selectField = field as SelectField;
      defaultValues[field.name] = selectField.defaultValue || '';
    } else if (field.type === 'multiselect') {
      const multiSelectField = field as MultiSelectField;
      defaultValues[field.name] = multiSelectField.defaultValues || [];
    } else if (field.type === 'dependentSelect') {
      const depField = field as DependentSelectField;
      const parentField = formConfig.fields.find(f => f.name === depField.dependsOn) as SelectField | undefined;
      const parentDefault = parentField?.defaultValue || '';
      defaultValues[field.name] = getDependentSelectDefault(depField, parentDefault);
    }

    const prefillValue = prefill?.[field.name];
    if (prefillValue && (field.type === 'text' || field.type === 'aiSuggestions' || field.type === 'select')) {
      defaultValues[field.name] = prefillValue;
    }
  });

  if (formConfig.allowImageGeneration) {
    defaultValues[IMAGE_GENERATION_ENABLED_FIELD] = false;
    defaultValues[IMAGE_DESCRIPTION_FIELD] = '';
  }

  if (formConfig.showAttachmentsHint) {
    defaultValues[ATTACHMENTS_HINT_FIELD] = '';
  }

  return defaultValues;
};

const fieldGridStyle = {
  mb: spaces.xs,
  boxSizing: 'border-box'
};

const selectMenuProps = {
  sx: { zIndex: 9999 + ' !important' },
};

const selectSx = {
  borderRadius: borderRadius.sm,
  border: `1px solid ${palette.components.input.outlined.enabledBorder}`,
};

export interface TemplatePromptFormFooterProps {
  onCancel: () => void;
  onSubmit: () => void;
  disabled: boolean;
}

export interface AIAssistantTemplatePromptFormProps {
  renderHeader?: (title: string, onCancel: () => void) => React.ReactNode;
  renderFooter: (props: TemplatePromptFormFooterProps) => React.ReactNode;
  renderDescription?: (description: string) => React.ReactNode;
  renderWrapper?: (children: React.ReactNode, footer: React.ReactNode) => React.ReactNode;
  renderContentWrapper?: (
    children: React.ReactNode, title: string, description: string,
  ) => React.ReactNode;
  contentStyles?: object;
}

export default function AIAssistantTemplatePromptForm({
  renderHeader,
  renderFooter,
  renderDescription,
  renderWrapper,
  renderContentWrapper,
  contentStyles,
}: AIAssistantTemplatePromptFormProps): JSX.Element | null {
  const { 
    processNewConversation,
    setActiveView,
    selectedTemplatePromptId,
    setSelectedTemplatePromptId,
    templateLaunchPrefill,
    dialogContentRef,
  } = useContext(aiAssistantContext);
  const { t } = useTranslation();
  const temporaryAttachments = useSelector(getTemporaryAttachments);
  const attachmentsReady = useSelector(areTemporaryAttachmentsReady);
  const imageRemainingLimit = useSelector(getAiGeneratedImagesRemainingLimit);
  const isImageQuotaReached = typeof imageRemainingLimit === 'number' && imageRemainingLimit <= 0;
  const clearTemporaryAttachments = useAction(clearTemporaryAttachmentsAction);
  const [links, setLinks] = useState<string[]>([]);
  const { match: { location: { pathname } } } = useRouter();
  const pageSource = getAIAssistantPageSource(pathname);
  
  const { isXxsScreen } = useScreenSize();
  
  const formConfig = useMemo(() => getFormConfig(selectedTemplatePromptId), [selectedTemplatePromptId]);

  // When the links section is hidden, ensure no stale link state leaks into the submission.
  useEffect(() => {
    if (formConfig?.hideAddLinks) {
      setLinks([]);
    }
  }, [formConfig?.hideAddLinks]);

  // Find dependent select fields and their parent field names
  const dependentSelectFields = useMemo((): DependentSelectField[] => {
    if (!formConfig) return [];
    return formConfig.fields.filter(
      (f): f is DependentSelectField => f.type === 'dependentSelect'
    );
  }, [formConfig]);

  const buildValidationSchema = (config: TemplateFormConfig) => {
    const schemaObject: Record<string, yup.Schema<any>> = {};

    config.fields.forEach(field => {
      if (field.required) {
        if (field.type === 'text' || field.type === 'aiSuggestions') {
          schemaObject[field.name] = yup.string()
            .required(field.requiredMessage
              ? t(field.requiredMessage)
              : `${t('Please fill a {{field}}', { field: t(field.label) })}`);
        } else if (field.type === 'select') {
          schemaObject[field.name] = yup.string()
            .required(`${t('Please select a {{field}}', { field: t(field.label) })}`);
        } else if (field.type === 'multiselect') {
          const multiSelectField = field as MultiSelectField;
          schemaObject[field.name] = yup.array()
            .min(multiSelectField.minSelections || 1,
              `${t('Please select at least one {{field}}', { field: t(field.label) })}`);
        }
      }
    });

    // Description is required only when the checkbox is checked.
    if (config.allowImageGeneration) {
      schemaObject[IMAGE_GENERATION_ENABLED_FIELD] = yup.boolean();
      schemaObject[IMAGE_DESCRIPTION_FIELD] = yup.string().when(
        IMAGE_GENERATION_ENABLED_FIELD,
        {
          is: true,
          then: yup.string().trim().required(`${t('Please describe the image')}`),
        },
      );
    }

    return yup.object(schemaObject);
  };

  // Both feed `useForm`, which pulls them into the identities of the setters it
  // returns (`setValue` closes over `triggerValidation` → `validationSchema`).
  // Rebuilding them per render would hand back a fresh `setValue` every time and
  // turn any effect that depends on it into a render loop.
  const validationSchema = useMemo(
    () => (formConfig ? buildValidationSchema(formConfig) : yup.object({})),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formConfig, t],
  );
  const defaultValues = useMemo(
    () => (formConfig ? buildDefaultValues(formConfig, templateLaunchPrefill) : {}),
    [formConfig, templateLaunchPrefill],
  );
  
  const {
    errors,
    handleSubmit,
    formState,
    register,
    watch,
    getValues,
    control,
    setValue,
    reset
  } = useForm({
    mode: 'onBlur',
    validationSchema,
    defaultValues
  });

  // The form is not remounted when the user switches templates (same component,
  // only the context id changes), but RHF reads `defaultValues` once at mount.
  // Re-seed the fields on every template switch so a multiselect field always
  // holds an array — a `Select multiple` crashes on a non-array value carried
  // over from a previous template that lacked that field.
  useEffect(() => {
    if (formConfig) {
      reset(buildDefaultValues(formConfig, templateLaunchPrefill));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplatePromptId, templateLaunchPrefill]);

  // Watch all field values for character counting and dependent selects
  const watchedValues = watch();

  // Auto-reset dependent selects when their parent value changes
  // (e.g. switching language from German→English auto-selects the default speaker for English).
  // Snapshots commit only after all fields are processed so fields sharing a trigger all reset.
  const parentValuesRef = useRef<Record<string, string>>({});
  useEffect(() => {
    const seenValues: Record<string, string> = { ...parentValuesRef.current };
    dependentSelectFields.forEach(depField => {
      const alsoResetOn = depField.alsoResetOn;
      const triggers = alsoResetOn
        ? [depField.dependsOn, alsoResetOn.field]
        : [depField.dependsOn];
      const changed = triggers.some(trigger => {
        const prevValue = parentValuesRef.current[trigger];
        if (prevValue === undefined || watchedValues[trigger] === prevValue) {
          return false;
        }
        // The extra trigger resets only when it lands on its target value
        if (alsoResetOn && trigger === alsoResetOn.field) {
          return watchedValues[trigger] === alsoResetOn.value;
        }
        return true;
      });
      triggers.forEach(trigger => {
        seenValues[trigger] = watchedValues[trigger];
      });

      if (changed) {
        setValue(
          depField.name,
          getDependentSelectDefault(depField, watchedValues[depField.dependsOn]),
        );
      }
    });
    parentValuesRef.current = seenValues;
  }, [dependentSelectFields, watchedValues, setValue]);

  if (!formConfig) {
    return null;
  }

  const getMultiselectDisplayText = (field: MultiSelectField, selectedValues: string[]) => {
    if (selectedValues.length === 0) return '';
    if (selectedValues.length === 1) {
      const option = field.options.find(opt => opt.value === selectedValues[0]);
      return option?.label || selectedValues[0];
    }
    return field.displayLogic?.multipleItemsText || selectedValues.join(', ');
  };

  const handleFormSubmit = () => {
    // The footer button is disabled mid-upload (see its `disabled` prop below),
    // but Enter in a single-line field submits this form directly, bypassing it.
    if (!attachmentsReady) {
      return;
    }

    const promptParts = [`${t(formConfig.title)}`];
    const data = getValues();
    const dataToSend: Record<string, any> = {};
    formConfig.fields.forEach(field => {
      if (!isFieldVisible(field, data)) return;
      const value = data[field.name];
      if (value && (typeof value === 'string' || Array.isArray(value))) {
        let tranformedValue = value; 
        if (Array.isArray(value) && value.length > 0) {
          tranformedValue = value.map(v => t(v)).join(', ');
          promptParts.push(`${t(field.label)}: ${tranformedValue};`);
        } else if (typeof value === 'string' && value.trim()) {
          promptParts.push(`${t(field.label)}: ${t(value)};`);
        }
        const transformedName = field.name.replace(/ /g, '_').toLowerCase();
        dataToSend[transformedName] = tranformedValue;
      }
    });

    if (formConfig.allowImageGeneration && data[IMAGE_GENERATION_ENABLED_FIELD]) {
      const imageDescription = data[IMAGE_DESCRIPTION_FIELD];
      dataToSend.generate_image = true;
      if (typeof imageDescription === 'string' && imageDescription.trim()) {
        dataToSend.image_description = imageDescription.trim();
        promptParts.push(`${t('Image description')}: ${imageDescription.trim()};`);
      }
    }

    // The dedicated image template always generates an image, so force it.
    if (formConfig.templateId === TemplatePrompts.CREATE_IMAGE) {
      dataToSend.generate_image = true;
    }

    const prompt = promptParts.join('\n');
    const cleanedLinks = links.filter(link => link && link.trim());
    if (formConfig.templateId === TemplatePrompts.CREATE_PODCAST) {
      const podcastFormat = data[FieldName.PODCAST_FORMAT] as PodcastFormat;
      const podcastData: ConversationMessageContext['data'] = {
        subject: data[FieldName.SUBJECT],
        theme: data[FieldName.TOPIC],
        gradeLevel: parseInt(data[FieldName.CLASS_YEAR]),
        duration: podcastDurationMap[data[FieldName.PODCAST_DURATION] as PodcastDuration],
        language: podcastLanguageMap[data[FieldName.TARGET_LANGUAGE] as PodcastTargetLanguage],
        format: podcastFormatMap[podcastFormat],
        speakers: podcastFormat === PodcastFormat.DIALOG
          ? [data[FieldName.SPEAKER_1], data[FieldName.SPEAKER_2]]
          : [data[FieldName.SPEAKER_1]],
        extraInfo: data[FieldName.CONTENT_DESCRIPTION] || '',
      };
      const attachmentsHint = (data[ATTACHMENTS_HINT_FIELD] || '').trim();
      if (temporaryAttachments.length > 0 && attachmentsHint) {
        podcastData.attachmentsHint = attachmentsHint;
      }
      processNewConversation({
        message: prompt,
        messageContext: {
          templatePromptId: formConfig.templateId,
          data: podcastData,
          ...(
            temporaryAttachments.length > 0 ?
              { attachments: temporaryAttachments }
              : {}
          ),
        },
        activeViewOverride: ActiveView.PODCAST_LOADING,
      });
    } else {
      processNewConversation({
        message: prompt,
        messageContext: {
          templatePromptId: formConfig.templateId,
          data: dataToSend,
          ...(
            temporaryAttachments.length > 0 ?
              { attachments: temporaryAttachments }
              : {}
          ),
          ...(cleanedLinks.length > 0 && { links: cleanedLinks }),
        }
      });
    }

    clearTemporaryAttachments();
  };

  const onSubmit = handleSubmit(handleFormSubmit);

  const handleCancel = () => {
    setActiveView(ActiveView.FREE_DRAFT_ENTRY);
    setSelectedTemplatePromptId(null);
  };

  const footerElement = renderFooter({
    onCancel: handleCancel,
    onSubmit,
    // Blocked mid-upload: `temporaryAttachments` still holds the placeholder id.
    disabled: formState.isSubmitting || !attachmentsReady,
  });

  const renderField = (field: FormField) => {
    const fieldValue = watchedValues[field.name];

    switch (field.type) {    
      case 'text': {
        const textField = field as TextInputField;
        const characterCount = fieldValue?.length || 0;
        
        return (
          <Box data-testid='ai-assistant-template-prompt-form-input' >
            <LabelledTextField
              value={fieldValue || ''}
              label={`${t(field.label)}${field.required ? '*' : ''}`}
              placeholder={t(field.placeholder || '')}
              name={field.name}
              error={errors?.[field.name]}
              dataTestId={`ai-assistant-template-prompt-form-input-${field.name}`}
              multiline={textField.multiline}
              inputRef={register}
              maxLength={textField.maxLength}
              minRows={textField.minRows}
            />
            {textField.showCharacterCount && textField.maxLength && (
              <Typography variant='caption' sx={{ color: palette.text.secondary, mt: spaces.xs, display: 'block' }}>
                {characterCount}/{textField.maxLength} {t('characters')}
              </Typography>
            )}
          </Box>
        );
      }
      case 'aiSuggestions': {
        const aiSuggestionsField = field as AISuggestionsField;
        const characterCount = fieldValue?.length || 0;
        
        // Convert array of field names to object with actual values
        const aiParamsObject = aiSuggestionsField.aiParams.reduce((acc, fieldName) => {
          acc[fieldName] = watchedValues[fieldName];
          return acc;
        }, {} as Record<string, any>);
        
        return (
          <Box data-testid='ai-assistant-template-prompt-form-ai-suggestions'>
            <AISuggestionsFieldComponent
              label={`${t(aiSuggestionsField.label)}${aiSuggestionsField.required ? '*' : ''}`}
              generatorConfig={aiSuggestionsField.generatorConfig}
              aiParams={{ 
                ...aiParamsObject, 
                templateName: t(formConfig.title), 
                templateDescription: t(formConfig.description) }}
              multiline={aiSuggestionsField.multiline}
              minRows={aiSuggestionsField.minRows}
              maxLength={aiSuggestionsField.maxLength}
              placeholder={t(aiSuggestionsField.placeholder || '')}
              inputRef={register}
              name={field.name}
              error={errors?.[field.name]}
              dataTestId={`ai-assistant-template-prompt-form-input-${field.name}`}
              value={fieldValue || ''}
              pageSource={pageSource}
              onChange={(e: React.ChangeEvent<HTMLInputElement>, reason?: string) => {
                setValue(field.name, e.target.value);
                // Only track when user selects from AI suggestions dropdown
                if (reason === 'select') {
                  ampliTrackAISuggestionsAppliedEvent(
                    aiSuggestionsField.generatorConfig.promptType === 'topic' ? 'topic' : 'learning goal',
                  );
                }
              }}
            />
            {aiSuggestionsField.showCharacterCount && aiSuggestionsField.maxLength && (
              <Typography variant='caption' sx={{ color: palette.text.secondary, mt: spaces.xs, display: 'block' }}>
                {characterCount}/{aiSuggestionsField.maxLength} {t('characters')}
              </Typography>
            )}
          </Box>
        );
      }
      case 'select': {
        const selectField = field as SelectField;
        
        return (
          <Box
            data-testid='ai-assistant-template-prompt-form-select'
            sx={{ display: 'flex', flexDirection: 'column' }}>
            <FormControl fullWidth>
              <FormLabel
                sx={{ 
                  mb: 1, 
                  color: palette.text.primary,
                  '&.Mui-focused': {
                    color: palette.text.primary
                  }
                }}>{t(field.label)}</FormLabel>
              <Controller 
                control={control} 
                name={field.name} 
                as={
                  <Select
                    renderValue={() => t(fieldValue)}
                    size='small'
                    MenuProps={selectMenuProps}
                    sx={selectSx}
                  >
                    {selectField.options.map((option) => <MenuItem
                      key={option.value}
                      value={option.value}
                      disabled={option.disabled}
                    >
                      {t(option.label)}
                    </MenuItem>)}
                  </Select>
                }
              />
            </FormControl>
          </Box>
        );
      }
      case 'multiselect': {
        const multiSelectField = field as MultiSelectField;
        const selectedValues = fieldValue || [];
        const displayText = getMultiselectDisplayText(multiSelectField, selectedValues);
        
        return (
          <Box
            data-testid='ai-assistant-template-prompt-form-multiselect'
            sx={{ display: 'flex', flexDirection: 'column' }}>
            <FormControl fullWidth>
              <FormLabel
                sx={{ 
                  mb: 1, 
                  color: palette.text.primary,
                  '&.Mui-focused': {
                    color: palette.text.primary
                  }
                }}>{t(field.label)}</FormLabel>
              <Controller
                control={control}
                name={field.name}
                defaultValue={selectedValues}
                as={
                  <Select
                    multiple
                    value={selectedValues}
                    renderValue={() => t(displayText)}
                    size='small'
                    MenuProps={selectMenuProps}
                    sx={selectSx}
                  >
                    {multiSelectField.options.map((option) => {
                      const isSelected = selectedValues.includes(option.value);
                      const isLastSelected = selectedValues.length === (multiSelectField.minSelections || 1) 
                      && isSelected;
                    
                      return (
                        <MenuItem 
                          key={option.value} 
                          value={option.value}
                          disabled={isLastSelected}
                        >
                          <Checkbox 
                            checked={isSelected}
                            disabled={isLastSelected}
                            icon={<EmptyCheckbox />}
                            checkedIcon={<SelectedCheckbox />}
                          />
                          <ListItemText primary={t(option.label)} />
                        </MenuItem>
                      );
                    })}
                  </Select>
                }
              />
            </FormControl>
          </Box>
        );
      }
      case 'dependentSelect': {
        // Options change based on a parent field's value (e.g. speaker options depend on target language)
        const depField = field as DependentSelectField;
        const parentValue = watchedValues[depField.dependsOn] || '';
        const options = depField.optionGroups[parentValue] || [];
        const currentLabel = options.find(o => o.value === fieldValue)?.label || fieldValue || '';
        const siblingField = depField.excludeSelectedOf
          ? formConfig.fields.find(f => f.name === depField.excludeSelectedOf)
          : undefined;
        // A hidden sibling's leftover value must not block options here
        const excludedValue = siblingField && isFieldVisible(siblingField, watchedValues)
          ? watchedValues[siblingField.name]
          : undefined;

        return (
          <Box
            data-testid='ai-assistant-template-prompt-form-dependent-select'
            sx={{ display: 'flex', flexDirection: 'column' }}>
            <FormControl fullWidth>
              <FormLabel
                sx={{
                  mb: 1,
                  color: palette.text.primary,
                  '&.Mui-focused': {
                    color: palette.text.primary
                  }
                }}>{t(field.label)}</FormLabel>
              <Controller
                control={control}
                name={field.name}
                as={
                  <Select
                    renderValue={() => (currentLabel ? t(currentLabel) : '')}
                    size='small'
                    MenuProps={selectMenuProps}
                    sx={selectSx}
                  >
                    {options.map((option) => <MenuItem
                      key={option.value}
                      value={option.value}
                      disabled={option.disabled || option.value === excludedValue}
                    >
                      {t(option.label)}
                    </MenuItem>)}
                  </Select>
                }
              />
            </FormControl>
          </Box>
        );
      }
      default: {
        return null;
      }
    }
  };

  const fileUploadSection = (
    <>
      <Grid
        data-testid='ai-assistant-template-prompt-form-attachment-button'
        sx={{ position: 'relative', pl: spaces.xs, mt: spaces.xs }}
        xs={12}> 
        <AttachmentsButton fromControlledFieldScreen={true} label={t('Upload File')} />
      </Grid>
      {temporaryAttachments.length > 0 && (
        <Grid sx={{ px: spaces.xs , my: spaces.xs }}>
          <AIUploadFileList uploadedAttachments={temporaryAttachments} withRemoveButton />
        </Grid>
      )}
      {formConfig.showAttachmentsHint && temporaryAttachments.length > 0 && (
        <Grid item xs={12} sx={{ px: spaces.xs, my: spaces.xs }} data-testid='ai-assistant-attachments-hint'>
          <LabelledTextField
            value={watchedValues[ATTACHMENTS_HINT_FIELD] || ''}
            label={t('Notes on the uploads')}
            placeholder={t('e.g. only take the vocabulary list into account')}
            name={ATTACHMENTS_HINT_FIELD}
            error={errors?.[ATTACHMENTS_HINT_FIELD]}
            dataTestId='ai-assistant-attachments-hint-input'
            inputRef={register}
            maxLength={ATTACHMENTS_HINT_MAX_LENGTH}
          />
          <Typography variant='caption' sx={{ color: palette.text.secondary, mt: spaces.xs, display: 'block' }}>
            {(watchedValues[ATTACHMENTS_HINT_FIELD] || '').length}/{ATTACHMENTS_HINT_MAX_LENGTH} {t('characters')}
          </Typography>
        </Grid>
      )}
    </>
  );

  const imageGenerationSection = (
    <Grid item xs={12} sx={fieldGridStyle} data-testid='ai-assistant-image-generation-section'>
      <Tooltip
        title={isImageQuotaReached
          ? t('Your image quota for this month is used up. You can generate images again next month.')
          : ''}
        disableHoverListener={!isImageQuotaReached}
        disableFocusListener={!isImageQuotaReached}
        disableTouchListener={!isImageQuotaReached}
      >
        <span>
          <FormControlLabel
            disabled={isImageQuotaReached}
            label={t('Generate and insert image')}
            control={
              <Controller
                control={control}
                name={IMAGE_GENERATION_ENABLED_FIELD}
                valueName='checked'
                onChange={([event]) => event.target.checked}
                as={
                  <Checkbox
                    disabled={isImageQuotaReached}
                    icon={
                      <Box
                        sx={{
                          display: 'inline-flex',
                          position: 'relative',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            inset: '3px',
                            borderRadius: '6px',
                            backgroundColor: palette.background.paperElevation1,
                            zIndex: 0,
                          },
                          '& svg': { position: 'relative', zIndex: 1 },
                        }}
                      >
                        <EmptyCheckbox />
                      </Box>
                    }
                    checkedIcon={<SelectedCheckbox />}
                    data-testid='ai-assistant-image-generation-checkbox'
                    sx={{ '&.Mui-disabled': { opacity: 0.5 } }}
                  />
                }
              />
            }
          />
        </span>
      </Tooltip>
      {watchedValues[IMAGE_GENERATION_ENABLED_FIELD] && (
        <Box data-testid='ai-assistant-image-description' sx={{ mt: spaces.xs }}>
          <LabelledTextField
            value={watchedValues[IMAGE_DESCRIPTION_FIELD] || ''}
            label={`${t('Image description')}*`}
            placeholder={t('e.g. Illustration of the solar system')}
            name={IMAGE_DESCRIPTION_FIELD}
            error={errors?.[IMAGE_DESCRIPTION_FIELD]}
            dataTestId='ai-assistant-image-description-input'
            multiline
            inputRef={register}
            minRows={3}
          />
        </Box>
      )}
    </Grid>
  );

  const renderFormFields = () => {
    const getBaseGridSize = (field: FormField) => {
      const isFullWidth = fullWidthFieldTypes.includes(field.type) || (field as SelectField)?.fullWidth;
      
      if (isFullWidth) {
        return { xs: 12, sm: 12, md: 12 };
      }
      
      if (field.isHalfWidth && !isXxsScreen) {
        return field.isHalfWidth;
      }
      
      return { xs: 12, sm: 6, md: 6 };
    };
    
    return (
      <Grid 
        container 
        sx={{
          mb: 0,
          width: '100%',
          boxSizing: 'border-box',
          overflowX: 'hidden',
          '& .MuiOutlinedInput-root': { backgroundColor: palette.background.paperElevation1 },
        }}
        spacing={spaces.xs}
      >
        {formConfig.fields.filter(field => isFieldVisible(field, watchedValues)).map(field => {
          const baseGridSize = getBaseGridSize(field);
          return (
            <Grid
              item
              xs={baseGridSize.xs}
              sm={baseGridSize.sm}
              md={baseGridSize.md}
              key={field.name}
              sx={fieldGridStyle}
            >
              {renderField(field)}
            </Grid>
          );
        })}
        {formConfig.allowImageGeneration && imageGenerationSection}
        {!formConfig.hideFileUpload && fileUploadSection}
        {!formConfig.hideAddLinks && <AIAddLinks links={links} setLinks={setLinks} />}
        {formConfig.footerNote && (
          <Grid item xs={12} sx={fieldGridStyle}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: spaces.xs }}>
              <span aria-hidden='true'>✅</span>
              <Typography variant='body2' sx={{ color: palette.text.secondary }}>
                {t(formConfig.footerNote)}
              </Typography>
            </Box>
          </Grid>
        )}
      </Grid>
    );
  };

  const defaultContentStyles = contentStyles || {
    position: 'relative',
    padding: spaces.md,
    background: palette.background.paperElevation1,
    width: '100%',
    boxSizing: 'border-box',
  };

  const formElement = (
    <Box
      component='form'
      onSubmit={onSubmit}
      data-ai-template-form='true'
      sx={{ width: '100%', boxSizing: 'border-box', overflowX: 'hidden' }}
    >
      <Box
        component='fieldset'
        disabled={formState.isSubmitting}
        sx={{ border: 'none', padding: 0, margin: 0, width: '100%', boxSizing: 'border-box' }}
      >
        {renderFormFields()}
      </Box>
    </Box>
  );

  const dialogContentChildren = renderContentWrapper
    ? renderContentWrapper(formElement, t(formConfig.title), t(formConfig.description))
    : (
      <>
        {renderDescription && (
          <Box sx={{ mb: spaces.lg }}>
            {renderDescription(t(formConfig.description))}
          </Box>
        )}
        {formElement}
      </>
    );

  const formContent = (
    <>
      {renderHeader && renderHeader(t(formConfig.title), handleCancel)}
      <DialogContent
        data-testid='ai-assistant-template-prompt-form'
        sx={defaultContentStyles}
        ref={dialogContentRef}>
        {dialogContentChildren}
      </DialogContent>
      {!renderWrapper && footerElement}
    </>
  );

  if (renderWrapper) {
    return renderWrapper(formContent, footerElement) as JSX.Element;
  }

  return formContent;
}
